import React, { useState, useEffect } from 'react';
import { useCart } from '../../contexts/CartContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Trash2, ShoppingBag, Tag, AlertCircle, CheckCircle2, Check, Printer, Download, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import InvoicePrint from '../../components/InvoicePrint';
import AxiosDB from '@/config/axios2';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Cart = () => {
  const { cartItems, cartTotal, removeFromCart, fetchCart, loading } = useCart();
  const [couponCode, setCouponCode] = useState('');
  const [couponValidating, setCouponValidating] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [finalTotal, setFinalTotal] = useState(0);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [completedPurchase, setCompletedPurchase] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    calculateTotal();
  }, [cartItems, appliedCoupon]);



  const calculateTotal = () => {
    let total = 0;
    cartItems.forEach(item => {
      const duration = item.duration_months || 1;
      const discount = item.product.discount || 0;
      if (discount) {
        total += (parseFloat(item.product?.price) - parseFloat(item.product?.discount) || 0) * duration;
      } else {
        total += (item.product?.price || 0) * duration;
      }
    });

    if (appliedCoupon) {
      const couponDiscount = appliedCoupon.discount_type === 'percentage'
        ? total * (appliedCoupon.discount_value / 100)
        : Math.min(appliedCoupon.discount_value, total);
      total -= couponDiscount;
    }

    setFinalTotal(total);
  };

  const handleRemove = async (itemId) => {
    try {
      await removeFromCart(itemId);
      toast.success('Producto eliminado del carrito');
      setAppliedCoupon(null);
    } catch (error) {
      toast.error('Error al eliminar el producto');
    }
  };

  const handleDurationChange = async (itemId, newDuration) => {
    try {
      await AxiosDB.patch(`/cart/${itemId}/duration`, null, {
        params: { duration_months: newDuration }
      });
      await fetchCart();
      toast.success('Duración actualizada');
    } catch (error) {
      toast.error('Error al actualizar la duración');
    }
  };

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Ingresa un código de cupón');
      return;
    }

    setCouponValidating(true);
    try {
      // Calculate total before coupon
      let totalBeforeCoupon = 0;
      cartItems.forEach(item => {
        const discount = item.product?.discount;
        const duration = item.duration_months || 1;

        if (discount) {
          totalBeforeCoupon += ((parseFloat(item.product?.price) - parseFloat(item.product?.discount)) || 0) * duration;
        } else {
          totalBeforeCoupon += (item.product?.price || 0) * duration;
        }
      });

      const response = await AxiosDB.post(`/coupons/validate`, {
        code: couponCode,
        amount: totalBeforeCoupon
      });

      setAppliedCoupon(response.data);
      toast.success(`¡Cupón aplicado! Descuento: $${response.data.discount.toFixed(2)}`);
    } catch (error) {
      console.log(error)
      toast.error(error.response?.data?.detail || 'Cupón inválido');
      setAppliedCoupon(null);
    } finally {
      setCouponValidating(false);
    }
  };

  const handleCheckout = async () => {
    setCheckoutLoading(true);


    // Check billing info first
    try {
      const billingResponse = await AxiosDB.get(`/billing-info`);
      if (!billingResponse.data) {
        toast.error('Debes completar tu información de facturación primero');
        setCheckoutLoading(false);
        navigate('/dashboard/billing-info');
        return;
      }
    } catch (error) {
      setCheckoutLoading(false);
      toast.error('Error al verificar información de facturación');
      return;
    }

    try {
      const response = await AxiosDB.post(`/cart/checkout`, {
        coupon_code: appliedCoupon ? couponCode : null,
        payment: {
          metodo: 'Banco',
          divisa: 'DOP',
          status: 'Pendiente'
        }
      });

      // Get the first subscription's payment for the invoice
      if (response.data.pedido) {
        const pedido = response.data.pedido;

        // Fetch the payment with invoice data
        const paymentsResponse = await AxiosDB.get(`/payments`, { params: { pedido } });
        const latestPayment = paymentsResponse.data;

        if (latestPayment) {
          setCompletedPurchase(latestPayment);
          setShowConfirmation(true);
        }
      }

      toast.success('¡Compra procesada exitosamente!');
      await fetchCart();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar la compra');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handlePrintInvoice = () => {
    const data = document.getElementById("invoice-container").innerHTML;
    const pri = document.getElementById("ifmcontentstoprint").contentWindow;

    // 1️⃣ Obtener todos los estilos del documento actual
    let styles = "";
    document.querySelectorAll("link[rel='stylesheet'], style").forEach((style) => {
      styles += style.outerHTML;
    });


    pri.document.open();
    pri.document.write(`
    <html>
      <head>
        ${styles}
      </head>
      <body>
        ${data}
      </body>
    </html>
  `); pri.document.close();

    setTimeout(() => {
      pri.focus();
      pri.print();
    }, 900);
  };

  const handleDownloadInvoice = () => {

    toast.success('Usa la opción "Guardar como PDF" en el diálogo de impresión');
    handlePrintInvoice()
  };

  const getDiscountLabel = (precio, descuento) => {

    const des = ((descuento * 100) / precio)

    return `${des.toFixed(2)}% DESC`;
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]">Cargando...</div>;
  }

  return (
    <div className="space-y-6" data-testid="cart-page">
      <div>
        <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Carrito de Compras
        </h2>
        <p className="text-gray-600">Revisa y procesa tu compra</p>
      </div>

      {cartItems.length === 0 ? (
        <Card className="shadow-lg">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <ShoppingBag className="h-16 w-16 text-gray-300 mb-4" />
            <p className="text-xl font-semibold text-gray-500 mb-2">Tu carrito está vacío</p>
            <p className="text-gray-400 mb-6">Agrega productos para comenzar</p>
            <Button
              onClick={() => navigate('/dashboard/products')}
              data-testid="view-products-button"
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white'
              }}
            >
              Ver Productos
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => {
              const duration = item.duration_months || 1;
              const discount = parseFloat(item.product.discount);
              const discountLabel = getDiscountLabel(item.product.price, discount);
              const subtotal = ((item.product.price - discount) * duration);
              const originalSubtotal = ((item.product.price) * duration);

              return (
                <Card key={item.id} className="shadow-lg" data-testid={`cart-item-${item.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                          {item.product.name} -{discount}-
                        </h3>
                        <p className="text-gray-600 mb-3">{item.product.description}</p>

                        {/* Features */}
                        <div className="mb-4 space-y-2">
                          <p className="text-sm font-semibold text-gray-700">Características:</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {item.product.features.slice(0, 4).map((feature, idx) => (
                              <div key={idx} className="flex items-start gap-2">
                                <Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                                <span className="text-sm text-gray-600">{feature}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Duration Selector */}
                        <div className="flex items-center gap-4 mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">Duración:</span>
                            <Select
                              value={duration.toString()}
                              onValueChange={(value) => handleDurationChange(item.id, parseInt(value))}
                            >
                              <SelectTrigger className="w-[200px]" data-testid={`duration-select-${item.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {[...Array(24)].map((_, i) => {
                                  const months = i + 1;
                                  return (
                                    <SelectItem key={months} value={months.toString()}>
                                      {months} {months === 1 ? 'mes' : 'meses'}
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          </div>
                          {discountLabel && (
                            <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-md text-xs font-semibold">
                              <TrendingUp className="h-3 w-3" />
                              {discountLabel}
                            </div>
                          )}
                        </div>

                        {/* Pricing */}
                        <div className="flex items-center gap-4 flex-wrap">
                          <div>
                            <span className="text-sm text-gray-500">Precio/mes: </span>
                            <span className="font-semibold">${item.product.price.toFixed(2)}</span>
                          </div>
                          <div>
                            <span className="text-sm text-gray-500">Subtotal:  </span>
                            {discount && discount > 0 ? (
                              <>
                                <span className="line-through text-gray-400 mr-2">{Intl.NumberFormat("en-US", {
                                  style: "currency",
                                  currency: "USD",
                                }).format(originalSubtotal)} </span>
                                <span className="text-lg font-bold text-green-600">
                                  {Intl.NumberFormat("en-US", {
                                    style: "currency",
                                    currency: "USD",
                                  }).format(subtotal)}
                                </span>
                              </>
                            ) : (
                              <span className="text-lg font-bold text-purple-600">
                                ${subtotal.toFixed(2)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid={`remove-item-${item.id}`}
                        onClick={() => handleRemove(item.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-5 w-5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="space-y-4">
            {/* Coupon Card */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  <Tag className="h-5 w-5" />
                  Cupón de Descuento
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    placeholder="Código de cupón"
                    data-testid="coupon-input"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    disabled={appliedCoupon !== null}
                  />
                  <Button
                    onClick={handleValidateCoupon}
                    data-testid="apply-coupon-button"
                    disabled={couponValidating || appliedCoupon !== null}
                    variant="outline"
                  >
                    {couponValidating ? 'Validando...' : 'Aplicar'}
                  </Button>
                </div>
                {appliedCoupon && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg text-green-700">
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="text-sm font-medium">
                      Cupón aplicado: {appliedCoupon.discount_type === 'percentage'
                        ? `${appliedCoupon.discount_value}%`
                        : `$${appliedCoupon.discount_value}`} descuento
                    </span>
                  </div>
                )}

              </CardContent>
            </Card>

            {/* Summary Card */}
            <Card className="shadow-lg sticky top-24">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Resumen</CardTitle>
                <CardDescription>Detalles de tu compra</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Productos</span>
                  <span className="font-medium">{cartItems.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal (con descuentos por duración)</span>
                  <span className="font-medium">
                    ${Object.values(cartItems).reduce((sum, d) => sum + (parseFloat(d?.product.discount || 0) * d?.duration_months), 0).toFixed(2)}
                  </span>
                </div>
                {appliedCoupon && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Descuento cupón</span>
                    <span className="font-medium">
                      -${(Object.values(cartItems).reduce((sum, d) => sum + (parseFloat(d?.product.discount || 0) * d?.duration_months), 0) - finalTotal).toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold">Total</span>
                    <span className="text-2xl font-bold text-purple-600" data-testid="cart-total">
                      ${finalTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex flex-col gap-3">
                <Button
                  className="w-full"
                  data-testid="checkout-button"
                  onClick={handleCheckout}
                  disabled={checkoutLoading}
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    fontWeight: 600
                  }}
                >
                  {checkoutLoading ? 'Procesando...' : 'Procesar Compra'}
                </Button>
                <div className="flex items-start gap-2 text-xs text-gray-500">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>Asegúrate de completar tu información de facturación antes de procesar</span>
                </div>
              </CardFooter>
            </Card>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              <CheckCircle2 className="h-6 w-6 text-green-600" />
              ¡Compra Completada Exitosamente!
            </DialogTitle>
            <DialogDescription>
              Tu suscripción ha sido activada. Puedes imprimir o descargar tu factura.
            </DialogDescription>
          </DialogHeader>

          {completedPurchase && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Número de Factura:</p>
                    <p className="font-semibold">{completedPurchase.invoice_data?.invoice_number}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Total Pagado:</p>
                    <p className="font-semibold text-green-600 text-lg">
                      {completedPurchase?.divisa} {Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                      }).format(parseFloat(completedPurchase?.amount || 0))}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Fecha:</p>
                    <p className="font-semibold">
                      {new Date(completedPurchase.date).toLocaleDateString('es-ES')}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Método de Pago:</p>
                    <p className="font-semibold">{completedPurchase.payment_method}</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  className="flex-1"
                  onClick={handlePrintInvoice}
                  data-testid="print-invoice-button"
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white'
                  }}
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Imprimir Factura
                </Button>
                <Button
                  className="flex-1"
                  variant="outline"
                  onClick={handleDownloadInvoice}
                  data-testid="download-invoice-button"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Descargar PDF
                </Button>
              </div>

              <Button
                className="w-full"
                variant="outline"
                onClick={() => {
                  setShowConfirmation(false);
                  navigate('/dashboard/subscriptions');
                }}
              >
                Ver Mis Suscripciones
              </Button>
            </div>
          )}

          {/* Hidden invoice for printing */}
          {completedPurchase && (
            <div className="print-only">
              <InvoicePrint payment={completedPurchase} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <style jsx>{`
        @media print {
          .print-only {
            display: block !important;
          }
        }
        @media screen {
          .print-only {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default Cart;
